import React, { useMemo, useState, useRef } from "react";
import { Modal, Pagination } from "antd";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import DOMPurify from "dompurify";

const markdownStyles = `
  pb-3
  [&_h1]:mb-4 [&_h1]:mt-2 [&_h1]:text-[2em] [&_h1]:font-bold [&_h1]:text-[#1f1f1f] [&_h1]:leading-[1.2]
  [&_h2]:mb-3 [&_h2]:mt-6 [&_h2]:text-[1.5em] [&_h2]:font-semibold [&_h2]:text-[#262626] [&_h2]:leading-[1.3]
  [&_h3]:mb-2 [&_h3]:mt-5 [&_h3]:text-[1.25em] [&_h3]:font-semibold [&_h3]:text-[#262626] [&_h3]:leading-[1.4]
  [&_h4]:mb-2 [&_h4]:mt-4 [&_h4]:text-[1.15em] [&_h4]:font-bold [&_h4]:text-[#2d2d2d] [&_h4]:leading-[1.4]
  [&_p]:mb-4
  [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-8
  [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-8
  [&_li]:mb-1
  [&_li>ul]:mb-0 [&_li>ol]:mb-0
  [&_blockquote]:mb-4 [&_blockquote]:mt-4
  [&_code]:rounded [&_code]:bg-[#f0f0f0] [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.9em]
  [&_pre]:mb-4 [&_pre]:mt-4 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-[#f0f0f0] [&_pre]:p-3
  [&_pre_code]:bg-transparent [&_pre_code]:p-0
`;

type MarkdownModalProps = {
  open: boolean;
  title?: string;
  onClose: () => void;
  /** Optional: pass your own markdown string; defaults to Guide.md */
  md: string;
  /** How to split pages: HTML comment marker, heading split, etc. */
  splitMode?: "pagebreak" | "h1" | "hr" | "none";
};

const splitMarkdown = (md: string, mode: MarkdownModalProps["splitMode"]) => {
  if (mode === "h1") {
    // Split on level-1 headings (keeps the heading on each page)
    const parts = md.split(/^#\s+/m).filter(Boolean);

    if (parts.length <= 1) {
      return [md.trim()];
    }

    const processedParts = parts.map((part, index) => {
      return "# " + part.trim();
    });

    return processedParts;
  }
  if (mode === "hr") {
    // Horizontal rules (---) as page breaks
    const parts = md
      .split(/\n-{3,}\n/m)
      .map((s) => s.trim())
      .filter(Boolean);
    return parts.length ? parts : [md.trim()];
  }
  if (mode === "pagebreak") {
    const parts = md
      .split("<!-- pagebreak -->")
      .map((s) => s.trim())
      .filter(Boolean);
    return parts.length ? parts : [md.trim()];
  }
  return [md.trim()];
};

export default function MarkdownModal({
  open,
  onClose,
  title = "Guide",
  md,
  splitMode = "pagebreak",
}: MarkdownModalProps) {
  const [page, setPage] = useState(1);
  const modalBodyRef = useRef<HTMLDivElement>(null);

  const pages = useMemo(() => {
    const source = DOMPurify.sanitize(md.trim());
    return splitMarkdown(source, splitMode);
  }, [md, splitMode]);

  const safePage = Math.min(Math.max(page, 1), pages.length);

  return (
    <Modal
      destroyOnClose
      footer={null}
      open={open}
      styles={{ body: { maxHeight: "70vh", overflow: "hidden", display: "flex", flexDirection: "column" } }}
      title={title}
      width={900}
      onCancel={onClose}
    >
      <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        <div className={markdownStyles} style={{ paddingRight: "7px" }} ref={modalBodyRef}>
          <ReactMarkdown
            components={{
              a: (props) => <a {...props} rel="noreferrer" target="_blank" />,
              img: (props) => <img {...props} style={{ maxWidth: "100%" }} />,
            }}
            rehypePlugins={[rehypeRaw]}
            remarkPlugins={[remarkGfm]}
          >
            {pages[safePage - 1]}
          </ReactMarkdown>
        </div>
      </div>

      {pages.length > 1 && (
        <div style={{ paddingTop: "12px", borderTop: "1px solid #f0f0f0" }}>
          <Pagination
            current={safePage}
            pageSize={1}
            showSizeChanger={false}
            style={{ display: "flex", justifyContent: "flex-end" }}
            total={pages.length}
            onChange={(p) => {
              setPage(p);
              // Scroll to top of modal content after pagination
              const contentArea = modalBodyRef.current?.parentElement;
              if (contentArea) {
                contentArea.scrollTop = 0;
              }
            }}
          />
        </div>
      )}
    </Modal>
  );
}
